import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Projects', path: '/projects' },
  { name: 'Contact', path: '/contact' },
]

const serviceHighlights = [
  'Surveying',
  'Mapping',
  'GIS/LIS',
  'Remote Sensing',
  'Construction',
  'Engineering',
  'Project Management',
  'Property Consultant',
]

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const { pathname } = useLocation()
  const overHero = pathname === '/' && !scrolled

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0
      setScrollProgress(progress)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div className="bg-black text-white overflow-x-auto">
        <div className="section-padding max-w-7xl mx-auto min-w-max">
          <div className="h-8 flex items-center justify-center gap-2 whitespace-nowrap text-[10px] sm:text-xs font-medium tracking-wide">
            {serviceHighlights.map((service, index) => (
              <span key={service} className="flex items-center gap-2">
                {index > 0 && <span className="text-accent">|</span>}
                <span>{service}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={`relative transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-neutral-200 py-3'
          : 'bg-transparent py-5'
      }`}>
        {/* Scroll Progress Bar */}
        <div className="absolute bottom-0 left-0 h-[2px] bg-accent transition-all duration-150" style={{ width: `${scrollProgress}%` }} />

        <div className="section-padding max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={overHero ? '/logo_white.png' : '/logo.png'}
              alt="Advict Spatial Consult"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`relative font-medium text-sm transition-colors duration-300 ${
                  pathname === link.path
                    ? 'text-accent'
                    : overHero
                    ? 'text-white/80 hover:text-white'
                    : 'text-neutral-700 hover:text-black'
                }`}
              >
                {link.name}
                {pathname === link.path && (
                  <motion.span
                    layoutId="navbar-underline"
                    className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/contact"
              className={`border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                overHero
                  ? 'border-white/40 text-white hover:border-white hover:bg-white hover:text-black'
                  : 'border-black text-black hover:bg-black hover:text-white'
              }`}
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-neutral-100 transition-colors"
          >
            {isOpen ? (
              <X className={`w-6 h-6 ${overHero ? 'text-white' : 'text-black'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${overHero ? 'text-white' : 'text-black'}`} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 pb-4 border-t border-neutral-200 bg-white rounded-lg"
            >
              <div className="flex flex-col gap-2 pt-4">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className={`block py-2 px-4 rounded-lg font-medium transition-colors ${
                        pathname === link.path
                          ? 'bg-accent/10 text-accent'
                          : 'text-neutral-700 hover:bg-neutral-100'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.1 }}
                  className="pt-2 px-4"
                >
                  <Link
                    to="/contact"
                    className="block text-center bg-accent hover:bg-accent-dark text-black px-6 py-3 rounded-full font-semibold text-sm transition-all"
                  >
                    Get a Quote
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </motion.nav>
  )
}
